import * as React from 'react';
import { Box, Flex, FlexProps, Link, LinkProps, Text } from '@ttoss/ui';
import { DEFAULT_STEP_OPTIONS } from '../../constants/onboarding';
import { Icon } from '@iconify/react';
import { IconOpenPlantsResult } from '../Icons';
import {
  PROPERTIES_MODULE,
  SEARCH_MODULE,
  USER_MODULE,
} from '../../constants/featureFlag';
import { Steps } from 'intro.js-react';
import { useNavigation } from '../../providers/NavigationProvider/NavigationProvider';
import { useUser } from '../../modules/User/useUser';
import BackgroundFooterMenu from '../BackgroundFooterMenu/BackgroundFooterMenu';
import FullLogo from '../FullLogo/FullLogo';
import HorizontalLine from '../HorizontalLine/HorizontalLine';

type HeaderProps = {
  onClickLogo?: () => void;
  onCloseMenu?: () => void;
};

const Header = ({ onClickLogo, onCloseMenu }: HeaderProps) => {
  return (
    <Flex sx={{ justifyContent: 'space-between', padding: 6 }}>
      <Flex
        aria-label="logo"
        onClick={onClickLogo}
        sx={{ width: '102px', cursor: 'pointer' }}
      >
        <FullLogo width="100%" height="auto" />
      </Flex>
      <Flex
        as="button"
        onClick={onCloseMenu}
        aria-label="close-button"
        sx={{
          backgroundColor: 'transparent',
          border: 'none',
          alignItems: 'center',
          fontSize: 4,
          cursor: 'pointer',
        }}
      >
        <Icon icon="ic:round-close" />
      </Flex>
    </Flex>
  );
};

type MenuItemProps = {
  icon?: React.ReactElement;
  children?: React.ReactNode;
} & LinkProps;

export const ONBOARD_KEY = 'onboarding:Menu';

const MenuItem = ({ children, icon, sx, ...props }: MenuItemProps) => {
  return (
    <Link
      sx={{
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none',
        cursor: 'pointer',
        color: 'text',
        ...sx,
      }}
      {...props}
    >
      <Text sx={{ display: 'flex', fontSize: 4, marginRight: 6 }}>{icon}</Text>
      <Text sx={{ fontSize: 3, fontFamily: 'heading' }}>{children}</Text>
    </Link>
  );
};

export type TypeItemMenu = {
  href: string;
  label: string;
  className?: string;
};

export type TypeItemMenuWithIcon = TypeItemMenu & {
  icon: React.ReactElement;
};

export type MenuProps = FlexProps & {
  onClickLogo?: () => void;
  onCloseMenu: () => void;
  onLogout?: () => void;
};

const useMenuItems = () => {
  const { user } = useUser();

  const menuItems = React.useMemo(() => {
    return [
      {
        label: 'Recomendações de Espécies/Clones',
        href: '/recomendacoes',
        icon: <IconOpenPlantsResult />,
      },
      {
        label: 'Atlas Biofísico',
        href: '/biofisico',
        icon: <Icon icon="ic:outline-layers" />,
      },
      PROPERTIES_MODULE && {
        label: 'Meus locais',
        href: '/meus-locais',
        icon: <Icon icon="ic:baseline-bookmark-border" />,
      },
      SEARCH_MODULE && {
        label: 'Buscar propriedades/compradores',
        href: '/buscar-propriedades',
        icon: <Icon icon="ic:baseline-search" />,
      },
      user.isProducer &&
        PROPERTIES_MODULE && {
          label: 'Minhas propriedades',
          href: '/minhas-propriedades',
          icon: <Icon icon="ic:baseline-list" />,
        },
      USER_MODULE && {
        label: 'Minha conta',
        href: '/minha-conta',
        icon: <Icon icon="ic:outline-account-circle" />,
      },
    ].filter((item) => {
      return !!item;
    });
  }, [user.isProducer]);

  return { menuItems };
};

const listMenuFooterItems = [
  {
    label: 'Conheça o Projeto',
    href: 'https://www.siflorcerrado.com.br/',
    className: 'conheca-o-projeto',
  },
  {
    label: 'Links Úteis',
    href: 'https://www.siflorcerrado.com.br/links-uteis',
    className: 'links-uteis',
  },
  {
    label: 'Créditos',
    href: 'https://www.siflorcerrado.com.br/creditos',
    className: 'creditos',
  },
  {
    label: 'Fontes',
    href: 'https://www.siflorcerrado.com.br/fonte-de-dados',
    className: 'fontes',
  },
];

const Menu = ({ onClickLogo, onCloseMenu, onLogout, sx }: MenuProps) => {
  const { menuItems } = useMenuItems();

  const { popUpSpecialistWasClosed } = useNavigation();

  const [state, setState] = React.useState({
    stepsEnabled: false,
    initialStep: 0,
    steps: [
      {
        intro:
          'Essa são as funcionalidades do Siflor Cerrado! Mas antes de finalizar, vamos conhecer mais sobre o projeto!',
      },
      {
        element: '.conheca-o-projeto',
        intro: 'Esse é nosso site, nos visite sempre',
      },
      {
        element: '.links-uteis',
        intro:
          'Nos links uteis você encontrará muito mais informações que ajudará você a implantar seu sistema de cultivo, como livros de apoio e outros links',
      },
      {
        element: '.creditos',
        intro:
          'Aqui você encontrará toda a equipe que fez parte desse grande projeto',
      },
      {
        element: '.fontes',
        intro:
          'Em fontes você encontrará a origem dos dados que nos ajudaram a compor o projeto de pesquisa, e consequentemente, essa plataforma',
      },
    ],
  });

  React.useEffect(() => {
    const showOnboarding = !localStorage.getItem(ONBOARD_KEY);

    if (popUpSpecialistWasClosed && showOnboarding) {
      setTimeout(() => {
        setState((prev) => {
          return { ...prev, stepsEnabled: true };
        });
      }, 1000);
    }
  }, [popUpSpecialistWasClosed]);

  const onExit = () => {
    setState((prev) => {
      return { ...prev, stepsEnabled: false };
    });

    localStorage.setItem(ONBOARD_KEY, 'showed');
  };

  return (
    <Flex sx={{ flexDirection: 'column', ...sx }}>
      <Steps
        enabled={state.stepsEnabled}
        steps={state.steps}
        initialStep={state.initialStep}
        onExit={onExit}
        options={DEFAULT_STEP_OPTIONS}
      />

      <Header onClickLogo={onClickLogo} onCloseMenu={onCloseMenu} />

      <HorizontalLine />

      <Box sx={{ paddingX: 6, paddingY: 8 }}>
        {menuItems?.map((itemMenu, idx) => {
          return (
            <MenuItem
              key={itemMenu.label}
              aria-label={itemMenu.label}
              href={itemMenu.href}
              icon={itemMenu.icon}
              sx={{ marginBottom: idx === menuItems.length - 1 ? 0 : 8 }}
            >
              {itemMenu.label}
            </MenuItem>
          );
        })}
      </Box>

      <HorizontalLine />

      <Box sx={{ paddingLeft: 8, paddingTop: 8, zIndex: 1 }}>
        {listMenuFooterItems.map((itemMenuFooter, idx) => {
          return (
            <MenuItem
              key={`menu-footer-item-${idx + 1}`}
              aria-label={itemMenuFooter.label}
              href={itemMenuFooter.href}
              className={itemMenuFooter.className}
              sx={{
                marginBottom: idx === listMenuFooterItems.length - 1 ? 9 : 8,
              }}
            >
              {itemMenuFooter.label}
            </MenuItem>
          );
        })}

        <MenuItem onClick={onLogout}>Sair</MenuItem>
      </Box>
      <Box
        sx={{
          marginTop: [-11, 'auto'],
          marginBottom: [null, 7],
          position: 'relative',
        }}
      >
        <Box sx={{ display: [null, 'none'] }}>
          <BackgroundFooterMenu width="100%" height="auto" />
        </Box>
        <Text
          sx={{
            fontSize: 2,
            fontFamily: 'heading',
            position: ['absolute', 'relative'],
            bottom: [5, 'unset'],
            left: [0, 'unset'],
            width: '100%',
            display: [null, 'block'],
            textAlign: 'center',
            color: ['background', 'text'],
          }}
        >
          Siflor Cerrado v.1.0 - Todos os direitos reservados
        </Text>
      </Box>
    </Flex>
  );
};

export default Menu;
